import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { aiCharacterService, AICharacter, AICharacterFormData } from '../../services/aiCharacterService';
import PageHeader from '../common/PageHeader';
import AICharacterForm from './AICharacterForm';
import './AICharacterEdit.css';

interface AICharacterEditProps {
  onToggleSidebar: () => void;
}

const AICharacterEdit: React.FC<AICharacterEditProps> = ({ onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [character, setCharacter] = useState<AICharacter | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditMode = Boolean(id);

  useEffect(() => {
    if (isEditMode && id) {
      fetchCharacter();
    }
  }, [id, isEditMode]);

  const fetchCharacter = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await aiCharacterService.getCharacterById(id);
      if (response.success && response.character) {
        setCharacter(response.character);
      } else {
        setError('Failed to fetch character details.');
      }
    } catch (err) {
      setError('An error occurred while fetching character details.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: AICharacterFormData) => {
    if (!user) {
      setError('You must be logged in.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && id) {
        // Update existing character
        const response = await aiCharacterService.updateCharacter({
          id: id,
          ...formData
        });

        if (response.success) {
          navigate(`/ai-characters/${id}`);
        } else {
          setError('Failed to update character.');
        }
      } else {
        // Create new character
        const response = await aiCharacterService.createCharacter(user.id.toString(), formData);
        
        if (response.success && response.id) {
          navigate(`/ai-characters/${response.id}`);
        } else {
          setError('Failed to create character.');
        }
      }
    } catch (err) {
      setError(isEditMode ? 'An error occurred while updating the character.' : 'An error occurred while creating the character.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (id) {
      navigate(`/ai-characters/${id}`);
    } else {
      navigate('/ai-characters');
    }
  };

  if (loading) {
    return (
      <div className="ai-character-edit">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-title">/ai-characters</h1>}
        />
        <div className="ai-character-edit-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !character) {
    return (
      <div className="ai-character-edit">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-title">/ai-characters</h1>}
        />
        <div className="ai-character-edit-content">
          <p className="ai-character-edit-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-character-edit">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-title">{id ? 'Edit Character' : 'New Character'}</h1>}
      />
      <div className="ai-character-edit-content">
        {error && <div className="ai-character-edit-error">{error}</div>}
        <AICharacterForm
          character={character || undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={saving}
        />
      </div>
    </div>
  );
};

export default AICharacterEdit;

